/**
 * Remove what an OAuth round-trip leaves behind on web.
 *
 * ── What is left behind, and by whom ────────────────────────────────────────
 *
 * `expo-web-browser`'s web implementation completes a popup auth session by
 * writing the popup's **full current URL** into `localStorage`:
 *
 *     window.localStorage.setItem(getOriginUrlHandle(handle), window.location.href)
 *
 * On the implicit flow that URL is `https://…/#state=…&id_token=eyJhbGciOi…` —
 * the raw Google ID token, in durable per-origin storage, readable by any script
 * that later runs on this origin.
 *
 * The library does clean it up, in `dismissPopup()`, but that runs in the
 * **opener** and only once the flow reaches an outcome. Any path that does not —
 * the popup being severed from its opener, the opener being closed first, a tab
 * discarded mid-flow — leaves the token there indefinitely. That is not
 * hypothetical: it is exactly what the `Cross-Origin-Opener-Policy: same-origin`
 * defect did on staging on 2026-08-10, and the token outlived the session that
 * produced it.
 *
 * The tokens are short-lived (Google issues one hour) but a bearer credential in
 * `localStorage` is worth removing the moment it has been used, not on the next
 * successful sign-in.
 *
 * ── Scope ───────────────────────────────────────────────────────────────────
 *
 * Web only, because none of it exists elsewhere: native has no `localStorage`
 * and no address bar. Everything here is a no-op when the platform does not
 * supply the API rather than being guarded by a platform check at every call
 * site, so the caller stays readable.
 *
 * The functions take their dependencies as arguments so the behaviour can be
 * tested without a browser — see `tests/mobile/oauth-residue.test.ts`.
 */

/** The keys `expo-web-browser` writes. `getHandle()` is a fixed string there. */
const HANDLE_KEY = "ExpoWebBrowserRedirectHandle";
const RESIDUE_PREFIXES = [
  "ExpoWebBrowser_OriginUrl_",
  "ExpoWebBrowser_RedirectUrl_",
];

/** The minimum of `Storage` this needs; `localStorage` satisfies it. */
export type KeyValueStore = Pick<
  Storage,
  "length" | "key" | "removeItem"
>;

/**
 * Every key holding OAuth round-trip state, including the one carrying the token.
 *
 * Collected before removing anything: removing during iteration reindexes
 * `Storage`, so a straight `for (i = 0; i < length; i++)` that deletes as it goes
 * skips every second match. That failure is silent — some keys go, some stay.
 */
export function residueKeys(
  store: KeyValueStore,
): string[] {
  const keys: string[] = [];

  for (let i = 0; i < store.length; i += 1) {
    const key = store.key(i);

    if (key === null) continue;

    if (
      key === HANDLE_KEY ||
      RESIDUE_PREFIXES.some((prefix) =>
        key.startsWith(prefix),
      )
    ) {
      keys.push(key);
    }
  }

  return keys;
}

/** Remove them. Returns how many, so a caller can assert it did something. */
export function clearOAuthResidue(
  store: KeyValueStore | undefined = globalThis
    .localStorage,
): number {
  if (!store) return 0;

  const keys = residueKeys(store);

  for (const key of keys) {
    store.removeItem(key);
  }

  return keys.length;
}

/**
 * Strip an OAuth response fragment from the address bar without navigating.
 *
 * `history.replaceState` rather than assigning `location.hash`: assignment adds
 * a history entry and can retrigger routing, and setting it to `''` leaves a
 * bare `#` behind. This rewrites the current entry, so the token is not in the
 * back stack either.
 *
 * Only fires when the fragment actually carries a token response, so an ordinary
 * in-app anchor is never disturbed.
 */
export function clearOAuthFragment(
  location: Pick<Location, "hash" | "pathname" | "search"> | undefined = globalThis
    .location,
  history: Pick<History, "replaceState"> | undefined = globalThis
    .history,
): boolean {
  if (!location || !history) return false;

  const hash = location.hash ?? "";

  if (
    !/(^|[#&])(id_token|access_token|code)=/.test(
      hash,
    )
  ) {
    return false;
  }

  history.replaceState(
    null,
    "",
    `${location.pathname}${location.search}`,
  );

  return true;
}
